import { CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { Status, Priority, Horizon } from '@/types/enums'

/**
 * Icon size variants for status icons
 */
type IconSize = 'sm' | 'md' | 'lg'

const iconSizeClasses: Record<IconSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

/**
 * Get the appropriate icon component for a given status
 * @param status - The status value (can be Status enum or string)
 * @param size - The size of the icon ('sm', 'md', 'lg')
 * @returns JSX element with the appropriate icon
 */
export function getStatusIcon(status: Status | string | undefined | null, size: IconSize = 'md') {
  // Return a neutral icon if status is not provided
  if (!status) {
    return <AlertCircle className={`${iconSizeClasses[size]} text-gray-500`} />
  }

  const sizeClass = iconSizeClasses[size]

  // Normalize status to handle both enum and string values
  const normalizedStatus = typeof status === 'string' ? status : (status as Status)

  switch (normalizedStatus) {
    case Status.Done:
    case 'Done':
      return <CheckCircle2 className={`${sizeClass} text-green-500`} />
    case Status.InProgress:
    case 'In Progress':
      return <Clock className={`${sizeClass} text-blue-500`} />
    case Status.ToDo:
    case 'To Do':
      return <AlertCircle className={`${sizeClass} text-yellow-500`} />
    case Status.Draft:
    case 'Draft':
      return <AlertCircle className={`${sizeClass} text-gray-500`} />
    default:
      return <AlertCircle className={`${sizeClass} text-gray-500`} />
  }
}

/**
 * Get Tailwind color classes for a given priority level
 * @param priority - The priority level
 * @returns Tailwind CSS classes for text and background colors
 */
export function getPriorityColor(priority: Priority): string {
  switch (priority) {
    case Priority.None:
      return 'text-gray-500 bg-gray-50 dark:bg-gray-950'
    case Priority.High:
      return 'text-red-500 bg-red-50 dark:bg-red-950'
    case Priority.Medium:
      return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-950'
    case Priority.Low:
      return 'text-green-500 bg-green-50 dark:bg-green-950'
    default:
      return 'text-gray-500 bg-gray-50 dark:bg-gray-950'
  }
}

/**
 * Get Tailwind color classes for a given horizon level
 * @param horizon - The horizon level (time-based priority)
 * @returns Tailwind CSS classes for text and background colors
 */
export function getHorizonColor(horizon: Horizon): string {
  switch (horizon) {
    case Horizon.None:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950'
    case Horizon.Now:
      return 'text-red-600 bg-red-50 dark:bg-red-950'
    case Horizon.Next:
      return 'text-blue-600 bg-blue-50 dark:bg-blue-950'
    case Horizon.Someday:
      return 'text-purple-600 bg-purple-50 dark:bg-purple-950'
    case Horizon.Never:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950'
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950'
  }
}

/**
 * Date format variants
 */
export type DateFormat = 'short' | 'medium' | 'long' | 'full'

type DateFormatOptions = {
  month: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
  day: 'numeric' | '2-digit'
  year?: 'numeric' | '2-digit'
}

const dateFormatOptions: Record<DateFormat, DateFormatOptions> = {
  short: {
    month: 'short',
    day: 'numeric',
  },
  medium: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  long: {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
  full: {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
}

/**
 * Format a date with consistent styling across the application
 * @param date - The date to format
 * @param format - The format style ('short', 'medium', 'long', 'full')
 * @returns Formatted date string or null if date is not provided
 */
export function formatDate(date?: Date | string, format: DateFormat = 'medium'): string | null {
  if (!date) return null

  const dateObj = typeof date === 'string' ? new Date(date) : date

  return dateObj.toLocaleDateString('en-US', dateFormatOptions[format])
}
